import { useEffect, useState } from "react";
import { ShieldExclamationIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../common/Button";

interface Props {
  open: boolean;
  onAccept: () => Promise<void>;
  onDecline: () => void;
}

// Bump FEATURE_REQUEST_TERMS_VERSION on the server when this wording
// changes materially so everyone is asked again.
const TERMS = [
  "Your name and department are shown with everything you post, to every Police Narratives AI user, including officers at other agencies.",
  "Do not include case numbers, the names of suspects, victims, or witnesses, criminal justice information, or any other confidential detail. Describe the feature, not the case.",
  "Posting a request does not obligate us to build it. A status is our current plan, not a commitment or a timeline.",
  "You grant Police Narratives AI a perpetual, royalty-free license to use any idea or feedback you submit, without compensation or attribution.",
  "We may edit, merge, or remove posts that are off-topic or contain sensitive information.",
];

// Same checkbox-gated shell as the privilege attestation in the
// Investigations Files tab.
const TermsModal: React.FC<Props> = ({ open, onAccept, onDecline }) => {
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setChecked(false);
    setSaving(false);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !saving && onDecline();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const accept = async () => {
    setSaving(true);
    try {
      await onAccept();
    } finally {
      setSaving(false);
    }
  };

  return (
    <dialog open className="modal modal-open">
      <div className="modal-box max-w-lg p-0 overflow-hidden rounded-2xl">
        <div className="flex items-start gap-3 px-6 pt-6 pb-4">
          <div className="size-10 shrink-0 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center">
            <ShieldExclamationIcon className="size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-base-content leading-tight">Before you use Feature Requests</h3>
            <p className="text-sm text-base-content/60 mt-0.5">This board is shared with every department that uses Police Narratives AI.</p>
          </div>
        </div>
        <ol className="mx-6 mb-4 space-y-2 text-sm text-base-content/80 leading-6 list-decimal pl-5">
          {TERMS.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ol>
        <label className={cn("mx-6 mb-3 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm cursor-pointer transition-colors", checked ? "border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/40" : "border-base-300 bg-base-200/50 hover:bg-base-200")}>
          <input type="checkbox" className="checkbox checkbox-sm mt-0.5 [--chkbg:theme(colors.blue.500)] [--chkfg:white] checked:border-blue-500" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
          <span className="text-base-content/90 leading-6">I have read and agree to these terms.</span>
        </label>
        <p className="mx-6 mb-5 text-[11px] text-base-content/50">Your acceptance is recorded with your name and the time. You'll only be asked again if the terms change.</p>
        <div className="px-6 pb-6 flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onDecline} disabled={saving}>
            Not now
          </Button>
          <Button variant="primary" size="sm" disabled={!checked || saving} onClick={accept}>
            {saving ? "Saving…" : "I agree"}
          </Button>
        </div>
      </div>
      <button type="button" className="modal-backdrop" onClick={() => !saving && onDecline()} aria-label="Close" />
    </dialog>
  );
};

export default TermsModal;
